// src/stores/leadPoolStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { collection, query, where, orderBy, limit } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from './userStore' // Import user store
import { isLeadOrUnstamped } from '@/utils/leadRule'

export const useLeadPoolStore = defineStore('leadPool', () => {
  const leads = ref([])
  const isLoading = ref(true)
  let unsubscribe = null

  const leadCount = computed(() => leads.value.length)
  const userStore = useUserStore() // Get user store instance

  function subscribe() {
    if (unsubscribe) return

    const roles = userStore.roles
    // Check if user has either required role
    const hasAccess = roles.includes('sales') || roles.includes('admin')

    if (!hasAccess) {
      isLoading.value = false
      return
    }

    // F7 (calc/kiosk e2e ledger §5): the pool is the UNION LeadPoolPage renders — setter leads
    // PLUS website/kiosk leads (leadPoolStatus 'available'). A single createdBySetter query left
    // public leads invisible to AlertsIndicator + leadManagement. Do NOT stamp createdBySetter on
    // website leads instead — claimLead attribution relies on the distinction.
    const qSetter = query(
      collection(db, 'customers'),
      where('createdBySetter', '==', true),
      where('isClaimed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(200)
    )
    const qPool = query(
      collection(db, 'customers'),
      where('leadPoolStatus', '==', 'available'),
      where('isClaimed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(200)
    )

    const bySource = { setter: [], pool: [] }
    const applyMerge = () => {
      const merged = new Map()
      for (const lead of [...bySource.setter, ...bySource.pool]) merged.set(lead.id, lead)
      // Only leads, by the CRM's one rule (the journey stamp): the same filter as the Lead Pool page and claimLead.
      leads.value = [...merged.values()]
        .filter(isLeadOrUnstamped)
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
      isLoading.value = false
    }
    const onError = error => {
      console.error('Error listening to lead pool:', error)
      isLoading.value = false
    }

    const unsubSetter = onSnapshot(
      qSetter,
      snapshot => {
        bySource.setter = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        applyMerge()
      },
      onError
    )
    const unsubPool = onSnapshot(
      qPool,
      snapshot => {
        bySource.pool = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        applyMerge()
      },
      onError
    )
    unsubscribe = () => {
      unsubSetter()
      unsubPool()
    }
  }

  function unsubscribeStore() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    leads.value = []
  }

  return {
    leads,
    isLoading,
    leadCount,
    subscribe,
    unsubscribe: unsubscribeStore,
  }
})
